import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import RpPanelWrap from '../../util/RpPanelWrap';

type FBType = 'bug' | 'idea' | 'question' | 'other';

const TYPES: { key: FBType; label: string; emoji: string }[] = [
  { key: 'bug',      label: 'Bug',      emoji: '🐛' },
  { key: 'idea',     label: 'Idea',     emoji: '💡' },
  { key: 'question', label: 'Question', emoji: '❓' },
  { key: 'other',    label: 'Other',    emoji: '⭐' },
];

interface Props {
  userEmail?: string;
  onClose: () => void;
}

const FeedbackPanel: React.FC<Props> = ({ userEmail, onClose }) => {
  const [type, setType] = useState<FBType>('bug');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const reset = () => {
    setType('bug'); setMessage(''); setRating(null); setFile(null); setSent(false);
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('type', type);
      fd.append('message', message.trim());
      fd.append('page', location.pathname);
      if (userEmail) fd.append('userEmail', userEmail);
      if (rating) fd.append('rating', String(rating));
      if (file) fd.append('attachment', file);
      await axios.post('feedback', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSent(true);
      setTimeout(() => { onClose(); reset(); }, 2500);
    } catch {
      enqueueSnackbar('Failed to send feedback', { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleScreenshot = useCallback(async () => {
    try {
      const html2canvas = (window as any).html2canvas;
      if (html2canvas) {
        const canvas = await html2canvas(document.body, { scale: 0.5 });
        canvas.toBlob((blob: Blob) => {
          if (blob) setFile(new File([blob], 'screenshot.png', { type: 'image/png' }));
        });
      } else {
        enqueueSnackbar('Use "Attach file" to upload a screenshot manually', { variant: 'info' });
      }
    } catch {
      enqueueSnackbar('Screenshot failed, please attach manually', { variant: 'warning' });
    }
  }, [enqueueSnackbar]);

  return (
    <RpPanelWrap title="Send Feedback" icon="💬" iconBg="rgba(37,99,235,.12)" onClose={onClose}>
      {sent ? (
        <div style={{ padding: '48px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 56 }}>🎉</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-new)' }}>Thank you!</div>
          <div style={{ fontSize: 12, color: 'var(--text2-new)' }}>Your feedback has been received.</div>
        </div>
      ) : (
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Type selector */}
          <div style={{ display: 'flex', gap: 6 }}>
            {TYPES.map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setType(t.key)}
                style={{
                  flex: 1, padding: '8px 4px', borderRadius: 8, cursor: 'pointer',
                  border: type === t.key ? '1.5px solid rgba(37,99,235,.5)' : '1.5px solid var(--border-new)',
                  background: type === t.key ? 'rgba(37,99,235,.1)' : 'var(--surface2)',
                  color: type === t.key ? '#2563eb' : 'var(--text2-new)',
                  fontSize: 10, fontWeight: 700, transition: 'all .15s',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                }}
              >
                <span style={{ fontSize: 18 }}>{t.emoji}</span>
                {t.label}
              </button>
            ))}
          </div>

          {/* Text */}
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Describe the issue or idea..."
            rows={5}
            style={{
              width: '100%', border: message ? '1px solid rgba(37,99,235,.4)' : '1px solid var(--border-new)',
              borderRadius: 9, padding: 10, fontSize: 13,
              fontFamily: 'inherit', color: 'var(--text-new)', resize: 'vertical', outline: 'none',
              background: 'var(--surface2)', transition: 'border-color .15s', boxSizing: 'border-box',
            }}
            onFocus={e => e.currentTarget.style.borderColor = 'rgba(37,99,235,.5)'}
            onBlur={e => e.currentTarget.style.borderColor = message ? 'rgba(37,99,235,.4)' : 'var(--border-new)'}
          />

          {/* Attachments */}
          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              border: file ? '1.5px solid rgba(37,99,235,.4)' : '1.5px dashed var(--border-new)',
              background: file ? 'rgba(37,99,235,.07)' : 'var(--surface2)',
              color: file ? '#2563eb' : 'var(--text2-new)', transition: 'all .15s',
            }}>
              {file ? `📎 ${file.name.slice(0, 20)}${file.name.length > 20 ? '…' : ''}` : '📎 Attach image'}
              <input ref={fileRef} type="file" title="Attach image" accept="image/*" style={{ display: 'none' }}
                onChange={e => setFile(e.target.files?.[0] ?? null)} />
            </label>
            <button type="button" onClick={handleScreenshot}
              style={{
                padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                border: '1.5px dashed var(--border-new)', background: 'var(--surface2)',
                color: 'var(--text2-new)', transition: 'all .15s',
              }}
              title="Capture screenshot"
            >📸</button>
          </div>

          {/* Rating */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text2-new)', flexShrink: 0 }}>Satisfaction:</span>
            <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
              {['😞','😐','🙂','😊','🤩'].map((em, i) => (
                <span key={i} onClick={() => setRating(i + 1)}
                  style={{
                    fontSize: 22, cursor: 'pointer',
                    filter: rating === i + 1 ? 'drop-shadow(0 0 5px #fbbf24)' : 'grayscale(.3)',
                    transition: 'filter .15s',
                    opacity: rating && rating !== i + 1 ? 0.5 : 1,
                  }}>{em}</span>
              ))}
            </div>
          </div>

          {/* Submit */}
          <button type="button" onClick={handleSubmit} disabled={!message.trim() || sending}
            style={{
              width: '100%', padding: '11px', borderRadius: 9, border: 'none',
              background: message.trim() ? 'linear-gradient(135deg,#2563eb,#7c3aed)' : 'rgba(0,0,0,.1)',
              color: message.trim() ? '#fff' : 'var(--text3)',
              fontSize: 13, fontWeight: 700, cursor: message.trim() ? 'pointer' : 'not-allowed',
              boxShadow: message.trim() ? '0 4px 14px rgba(37,99,235,.3)' : 'none',
              transition: 'all .2s',
            }}
          >
            {sending ? 'Sending…' : 'Send Feedback →'}
          </button>

          <div style={{ fontSize: 10, color: 'var(--text2-new)', textAlign: 'center', opacity: .7 }}>
            {location.pathname}
          </div>
        </div>
      )}
    </RpPanelWrap>
  );
};

export default FeedbackPanel;
